import setTokens from "../utils/setTokens";

import logout from "~/utils/logout";

function parseJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const buffer = Buffer.from(base64, "base64");
    const jsonPayload = buffer.toString("utf8");
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

function getTokenExpiry(token) {
  const parsedToken = parseJwt(token);
  if(parsedToken) {
    return Math.max(parsedToken.exp - (Date.now() / 1000), 0);
  } else {
    return 0;
  }
}

export default async ({ app, store, redirect, route }) => {
  // Skip auth check for the impersonation bootstrap page — it handles
  // its own logic and sets the session cookies.
  if (route.path === "/impersonate" || route.name === "impersonate") {
    return;
  }

  if (route.name === "loggedout") {
    await store.commit("Auth/setData", {
      label: "resetPasswordUser",
      data: route.query.user,
    });
    await store.commit("Auth/setData", {
      label: "resetPasswordToken",
      data: route.query.token,
    });
    await store.commit("Auth/setData", { label: "resetPassword", data: true });
    app.$cookies.removeAll();
  } else {
    // Always set the current route path as pastUrl as long as we're not on
    // the login page here.
    if (route.path !== "/login" && route.path !== "/__webpack_hmr/client") {
      app.$cookies.set("pastUrl", route.path);
    }

    // Impersonation sessions bypass the normal auth flow — the Drupal backend
    // authenticates via the HttpOnly session cookie automatically sent with
    // every request. We just need to fetch the user's subscriptions and let
    // the page render.
    const impersonation = app.$cookies.get("impersonation_info");
    const impersonationSid = app.$cookies.get("impersonation_sid");
    if (impersonation && impersonationSid) {
      await store.commit("Auth/setData", { label: "isAuthorize", data: true });

      // Manually forward the impersonation cookie since SSR requests
      // don't automatically include browser cookies.
      const impHeaders = { Cookie: "impersonation_sid=" + impersonationSid };

      try {
        const subResp = await app.$axios({
          method: "GET",
          url: app.$config.apiURL + "/user-subscriptions",
          headers: impHeaders,
        });
        if (subResp && subResp.data) {
          store.commit("Auth/setSubscriptions", subResp.data);
        }
      } catch (e) {
        store.commit("Auth/setSubscriptions", { active: true, permissions: [], legacy_permissions: [] });
      }
      try {
        const profileResp = await app.$axios({
          method: "GET",
          url: app.$config.apiURL + "/user",
          headers: impHeaders,
        });
        if (profileResp && profileResp.data) {
          store.commit("Profile/setData", { label: "dataUser", data: profileResp.data });
        }
      } catch (e) { /* continue */ }
      return;
    }

    const accessToken = app.$cookies.get("accessToken");
    const refreshToken = app.$cookies.get("refreshToken");
    const idToken = app.$cookies.get("idToken");
    // const xCSRFToken = app.$cookies.get("xcsrf_token");

    // Currently the front-end doesn't handle X-CSRTokens of any kind
    // So wish to have a conversation with Kris about this
    // if (!xCSRFToken) {
    //     const responseToken = await app.$axios({
    //         method: "POST",
    //         url: `${config.env.BASE_URL}/session/token`,
    //         headers: {"Content-Type": "application/json"},
    //         progress: false,
    //         data: {refresh_token: refreshToken, grant_type: "refresh_token"}
    //         data: {refresh_token: refreshToken, grant_type: "refresh_token"}
    //     });
    // }
    let hasAnActiveSubscription = false;

    if (!accessToken && refreshToken) {
      let responseToken;
      try {
        responseToken = await app.$axios({
          method: "POST",
          url: `${app.$config.clientURL}/api/getToken`,
          headers: { "Content-Type": "application/json" },
          progress: false,
          data: { refresh_token: refreshToken, grant_type: "refresh_token" },
        });
      } catch (error) {
        console.error("Token refresh failed:", error.message || error);
        logout(app);
        return redirect("/login");
      }

      let data = responseToken.data.data;
      data = typeof data === "string" ? JSON.parse(data) : data;
      if (data.access_token && data.refresh_token) {
        await setTokens(
          app,
          data.access_token,
          data.refresh_token,
          data.expires_in,
          data.token_type,
          data.id_token
        );
        await app.$axios.setToken(data.access_token, data.token_type);
        await app.$axios.setHeader(
          "Authorization",
          `${data.token_type} ${data.access_token}`
        );

        await store.commit("Auth/setData", {
          label: "isAuthorize",
          data: true,
        });
        await store.commit("Auth/setToken", data);
        // await app
        //   .$axios({
        //     method: "GET",
        //     url: `${config.env.BASE_URL}/api/user-permissions`,
        //     headers: {
        //       "Content-Type": "application/json",
        //       Authorization: `Bearer ${store.state.Auth.access_token}`,
        //     },
        //   })
        //   .then((data) => {
        //     if (data.data) {
        //       store.commit("Auth/setPermissions", data.data);
        //     }
        //   })
        //   .catch((error) => console.error(error));
          await app
          .$axios({
            method: "GET",
            url: `${app.$config.baseURL}/api/user-subscriptions`,
            headers: {
              "Content-Type": "application/json",
            },
          })
          .then((data) => {
            if (data.data) {
              hasAnActiveSubscription = data.data.active ?? false;
              store.commit("Auth/setSubscriptions", data.data);
            }
          })
          .catch((error) => console.error(error));
          if (!hasAnActiveSubscription) {
            logout(app);
            return redirect("/login");
          }
        await store
          .dispatch("Profile/getData", {
            url: "/user",
            token: store.state.Auth.access_token,
          })
          .then(({ data }) => {
            store.commit("Profile/setData", { label: "dataUser", data });
          })
          .catch((error) => console.error(error));
      } else {
        return redirect("/login");
      }
    } else if (accessToken && refreshToken) {
      const expiresIn = getTokenExpiry(accessToken);

      // If access token is expired or about to expire (< 60s), refresh it
      let currentAccessToken = accessToken;
      let currentIdToken = idToken;
      let currentExpiresIn = expiresIn;
      let tokenType = "Bearer";

      if (expiresIn < 60) {
        try {
          const responseToken = await app.$axios({
            method: "POST",
            url: `${app.$config.clientURL}/api/getToken`,
            headers: { "Content-Type": "application/json" },
            progress: false,
            data: { refresh_token: refreshToken, grant_type: "refresh_token" },
          });

          let refreshData = responseToken.data.data;
          refreshData = typeof refreshData === "string" ? JSON.parse(refreshData) : refreshData;
          if (refreshData.access_token && refreshData.refresh_token) {
            currentAccessToken = refreshData.access_token;
            currentIdToken = refreshData.id_token;
            currentExpiresIn = refreshData.expires_in;
            tokenType = refreshData.token_type || "Bearer";

            await setTokens(
              app,
              currentAccessToken,
              refreshData.refresh_token,
              currentExpiresIn,
              tokenType,
              currentIdToken
            );
          } else {
            logout(app);
            return redirect("/login");
          }
        } catch (error) {
          console.error("Token refresh failed:", error);
          logout(app);
          return redirect("/login");
        }
      }

      const data = {
        access_token: currentAccessToken,
        expires_in: currentExpiresIn,
        refresh_token: refreshToken,
        token_type: tokenType,
        id_token: currentIdToken
      };

      await setTokens(
        app,
        data.access_token,
        data.refresh_token,
        data.expires_in,
        data.token_type,
        data.id_token
      );
      await app.$axios.setToken(data.access_token, data.token_type);
      await app.$axios.setHeader(
        "Authorization",
        `${data.token_type} ${data.access_token}`
      );

      await store.commit("Auth/setData", { label: "isAuthorize", data: true });
      await store.commit("Auth/setToken", data);
      // await app
      //   .$axios({
      //     method: "GET",
      //     url: `${config.env.BASE_URL}/api/user-permissions`,
      //     headers: {
      //       "Content-Type": "application/json",
      //       Authorization: `Bearer ${accessToken}`,
      //     },
      //   })
      //   .then((data) => {
      //     if (data.data) {
      //       store.commit("Auth/setPermissions", data.data);
      //     }
      //   })
      //   .catch((error) => console.error(error));
        await app
        .$axios({
          method: "GET",
          url: `${app.$config.baseURL}/api/user-subscriptions`,
          headers: {
            "Content-Type": "application/json"
          },
        })
        .then((data) => {
          if (data.data) {
            hasAnActiveSubscription = data.data.active ?? false;
            store.commit("Auth/setSubscriptions", data.data);
          }
        })
        .catch((error) => console.error(error));
      if (!hasAnActiveSubscription) {
        logout(app);
        return redirect("/login");
      }
      await store
        .dispatch("Profile/getData", { url: "/user", accessToken })
        .then(({ data }) => {
          store.commit("Profile/setData", { label: "dataUser", data });
        })
        .catch((error) => console.error(error));
    } else {
      return redirect("/login");
    }
  }
};
